import React, { useRef, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

function FloatingParticles({ count = 50, dark }) {
  const particles = useRef();

  useFrame(({ clock }) => {
    if (particles.current) {
      particles.current.children.forEach((particle, i) => {
        const time = clock.getElapsedTime();
        particle.position.x = Math.sin(time * 0.5 + i) * 8;
        particle.position.y = Math.cos(time * 0.3 + i) * 4;
        particle.position.z = Math.sin(time * 0.7 + i) * 2;
        particle.rotation.z = time * 0.5 + i;
      });
    }
  });

  return (
    <group ref={particles}>
      {Array.from({ length: count }).map((_, i) => (
        <mesh key={i} position={[
          (Math.random() - 0.5) * 16,
          (Math.random() - 0.5) * 8,
          (Math.random() - 0.5) * 4
        ]}>
          <boxGeometry args={[0.02, 0.02, 0.02]} />
          <meshBasicMaterial 
            color={dark ? "#a5b4fc" : "#1b1c4e"} 
            opacity={0.3} 
            transparent 
          />
        </mesh>
      ))}
    </group>
  );
}

function GlassyNetworkNode({ scrollY, dark }) {
  const group = useRef();
  
  useFrame(({ clock }) => {
    if (group.current) {
      const time = clock.getElapsedTime();
      group.current.rotation.y = scrollY.current * 0.002 + time * 0.1;
      group.current.rotation.x = Math.sin(scrollY.current * 0.001) * 0.2;
    }
  });
  
  // Node positions (hexagonal-ish)
  const nodes = [
    [1.1, 0.3, 0.2],
    [-1.05, 0.25, -0.1],
    [0.7, -0.95, 0.3],
    [-0.7, -0.95, -0.3],
    [0, 1.15, -0.25],
    [0, 0.1, 1.2],
  ];
  
  return (
    <group ref={group}>
      {/* Central glassy sphere */}
      <mesh>
        <sphereGeometry args={[0.5, 48, 48]} />
        <meshPhysicalMaterial
          color={dark ? "#e6e9f5" : "#1b1c4e"}
          roughness={0.09}
          metalness={0.85}
          clearcoat={1}
          clearcoatRoughness={0.05}
          transmission={0.85}
          thickness={1.2}
          ior={1.6}
          reflectivity={0.95}
          opacity={0.97}
          transparent
        />
      </mesh>
      {/* Connected nodes and links */}
      {nodes.map((pos, i) => (
        <group key={i}>
          {/* Link (cylinder) */}
          <mesh position={[(pos[0]) / 2, (pos[1]) / 2, (pos[2]) / 2]}>
            <cylinderGeometry args={[0.045, 0.045, Math.sqrt(pos[0]**2 + pos[1]**2 + pos[2]**2), 32]} />
            <meshPhysicalMaterial
              color={dark ? "#a5b4fc" : "#23244d"}
              roughness={0.13}
              metalness={0.8}
              clearcoat={1}
              clearcoatRoughness={0.07}
              transmission={0.8}
              thickness={1.1}
              ior={1.5}
              reflectivity={0.9}
              opacity={0.7}
              transparent
            />
          </mesh>
          {/* Small node sphere */}
          <mesh position={pos}>
            <sphereGeometry args={[0.19, 32, 32]} />
            <meshPhysicalMaterial
              color={dark ? "#818cf8" : "#1b1c4e"}
              roughness={0.13}
              metalness={0.8}
              clearcoat={1}
              clearcoatRoughness={0.07}
              transmission={0.8}
              thickness={1.1}
              ior={1.5}
              reflectivity={0.9}
              opacity={0.93}
              transparent
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export default function Background3D({ dark }) {
  const scrollY = useRef(0);
  
  useEffect(() => {
    const onScroll = () => { scrollY.current = window.scrollY; };
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  
  return (
    <div 
      className="background-3d"
      style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      zIndex: 10, pointerEvents: "none"
      }}
    >
      <Canvas 
        camera={{ position: [0, 0, 4.5], fov: 50 }}
        style={{ pointerEvents: "none" }}
        onPointerMissed={() => {}}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <ambientLight intensity={0.7} />
        <directionalLight position={[2, 4, 5]} intensity={1.2} />
        <pointLight position={[-5, 5, 5]} intensity={0.5} color={dark ? "#a5b4fc" : "#1b1c4e"} />
        <GlassyNetworkNode scrollY={scrollY} dark={dark} />
        <FloatingParticles count={30} dark={dark} />
      </Canvas>
      {/* Enhanced overlay for readability and glass effect */}
      <div style={{
        position: "absolute", top: 0, left: 0, width: "100vw", height: "100vh",
        background: dark 
          ? "radial-gradient(circle at 50% 50%, rgba(27,28,78,0.1) 0%, rgba(27,28,78,0.25) 100%)" 
          : "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)",
        backdropFilter: "blur(16px)",
        zIndex: 15
      }} />
    </div>
  );
} 